import { FC } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogProps,
  DialogTitle,
  Typography,
} from "@mui/material";
import { makeSxStyles } from "ui";
import { PendingDriverUpdateValues } from "../../../../state/route-planner-store";

export interface EditRouteDriverDialogProps
  extends Omit<
    DialogProps,
    "onClose" | "open" | "style" | "inputValue" | "handler"
  > {
  pendingDriverUpdate?: PendingDriverUpdateValues;
  onConfirm: (onClose: () => void) => void;
  onClose: () => void;
  open?: boolean;
  inputValue?: string;
  handler?: (value: string) => void;
  cancelText?: string;
  confirmText?: string;
  hasOtherRoute: boolean;
}

export const EditRouteDriverDialog: FC<EditRouteDriverDialogProps> = (
  props,
) => {
  const {
    pendingDriverUpdate,
    sx,
    onConfirm,
    onClose,
    open,
    cancelText = "Cancel",
    confirmText = "Assign",
    hasOtherRoute,
    ...rest
  } = props;
  const styles = useStyles();

  const title = hasOtherRoute ? `Change Driver` : "Confirm Driver Change";
  const assignedRoutes = pendingDriverUpdate?.assignedRoutes || [];
  const routeText = assignedRoutes.length === 1 ? "route" : "routes";
  const routesDescription =
    assignedRoutes.length > 1
      ? `${assignedRoutes.slice(0, -1).join(", ")}, ${assignedRoutes.slice(-1)}`
      : assignedRoutes[0];
  const getDriverDisplayName = () => {
    if (pendingDriverUpdate?.driverId === "Any") {
      return "Any";
    }
    return pendingDriverUpdate?.driverName.length
      ? pendingDriverUpdate.driverName
      : "No Driver";
  };

  const description = hasOtherRoute
    ? `Are you sure you want to assign ${pendingDriverUpdate?.routeName} to ${getDriverDisplayName()}? This driver already has ${assignedRoutes.length} ${routeText} assigned: ${routesDescription}.`
    : `Are you sure you want to assign ${pendingDriverUpdate?.routeName} to ${getDriverDisplayName()}?`;
  const confirmationText = hasOtherRoute ? confirmText : "Assign";
  const cancelingText = hasOtherRoute ? cancelText : "Cancel";

  return (
    <Dialog onClose={onClose} {...rest} sx={sx || {}} open={open || false}>
      <DialogTitle variant="titleLarge" sx={styles.dialogTitle}>
        {title}
      </DialogTitle>
      <Typography variant="bodyMedium" sx={styles.dialogDescription}>
        {description}
      </Typography>
      <DialogActions>
        <Button
          onClick={onClose}
          sx={styles.cancelButton}
          data-testid="cancel-button"
        >
          {cancelingText}
        </Button>
        <Button
          onClick={() => onConfirm(onClose)}
          variant="contained"
          sx={styles.confirmButton}
          data-testid="confirm-button"
        >
          {confirmationText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeSxStyles(({ palette: { grey, primary } }) => ({
  dialogTitle: {
    color: grey[100],
    lineHeight: "28px",
  },
  dialogDescription: {
    marginTop: "16px",
    marginBottom: "48px",
    color: grey[200],
  },
  cancelButton: {
    color: primary.main,
  },
  confirmButton: {
    backgroundColor: primary.main,
    color: "#00344E",
  },
}));
